import { CSSProperties, ReactNode, useState } from 'react'
import { Alert, Anchor, Box, Button, Group, Paper, SimpleGrid, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { IconLinkPlus } from '@tabler/icons-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useDeskDecision } from 'hooks/desk/useDeskDecision'
import { useQueryCities } from 'hooks/cities'
import { useQueryVenues } from 'hooks/venues'
import { useAppTranslation } from 'hooks/useTranslation'
import { ApplicationRow, EventRow, queryDeskCandidate, queryDeskTargetEvent } from 'supabase/desk'
import { DESK_MATCH_PANEL_ID } from './ChecksPanel'
import { CompareCell, CompareField, CompareRow, dateLocaleOf, fromApplication, fromEvent, sideBySideRows } from './compareRows'
import { mapDeskError } from './deskErrors'
import { deskTabName, showDeskError, showRejectedToast } from './RejectMenu'

type Translate = (key: string, options?: Record<string, unknown>) => string

/**
 * desk_queue's Update proposals rule (0035 desk_tab, D-02): a possible update
 * of a published event, not auto-rejected, not dismissed. The screen draws
 * the update proposal panel instead of the convert form for these.
 */
export const isUpdateProposal = (row: ApplicationRow): boolean =>
  row.match_label === 'possible_update' &&
  row.match_target_event_id !== null &&
  row.route !== 'auto_rejected' &&
  row.desk_match_dismissed_at === null

/**
 * The duplicate side-by-side shows on the candidate screen when the editor
 * labelled a possible duplicate, or a possible update that is not a live
 * update proposal, and Dominique has not dismissed it. A possible update
 * whose target is a pending candidate is not about a published event, so it
 * is worked like a duplicate (D-02 / D-14). So is one of a published event
 * that the Update proposals tab does not show (auto-rejected: opened from
 * the Discard pile or restored from it): without the panel it got the plain
 * convert form and approving duplicated the published event (review
 * AD-WR-06).
 */
export const showsMatchPanel = (row: ApplicationRow): boolean =>
  row.desk_match_dismissed_at === null &&
  (row.match_label === 'possible_duplicate' ||
    (row.match_label === 'possible_update' && !isUpdateProposal(row)))

/** The row labels of the closed compare set (UI-SPEC §E, §F). */
export const COMPARE_LABEL_KEYS: Record<CompareField, string> = {
  title: 'desk.columns.title',
  start: 'desk.columns.starts',
  end: 'desk.columns.ends',
  place: 'desk.columns.place',
  price: 'events.price',
  status: 'desk.fields.status'
}

type NamedRow = { id: number; name: string | null }

/**
 * A place as Dominique reads it: the venue's name and the town's name when
 * the ids resolve, else the side's own text. The desk compares by id
 * (compareRows), so the label is display only.
 */
export const placeLabelOf = (
  venueId: number | null,
  cityId: number | null,
  fallback: string | null,
  venues: NamedRow[] | undefined,
  cities: NamedRow[] | undefined
): string | null => {
  const venue = venueId === null ? null : venues?.find((row) => row.id === venueId)?.name?.trim() || null
  const city = cityId === null ? null : cities?.find((row) => row.id === cityId)?.name?.trim() || null
  if (!venue && !city) return fallback
  return [venue ?? fallback, city].filter((part): part is string => !!part).join(' · ')
}

export const candidatePlaceFallback = (row: ApplicationRow): string | null =>
  row.event_venue_location?.trim() || row.venue_village?.trim() || null

export const eventPlaceFallback = (row: Pick<EventRow, 'venue_name' | 'venue_city'>): string | null =>
  row.venue_name?.trim() || row.venue_city?.trim() || null

/** An event's title in the admin's language, falling back to the other one. */
export const eventTitleOf = (row: Pick<EventRow, 'title' | 'title_pt'>, language: string): string | null => {
  const own = language === 'pt-PT' ? row.title_pt : row.title
  const other = language === 'pt-PT' ? row.title : row.title_pt
  return own?.trim() || other?.trim() || null
}

export const cellText = (t: Translate, cell: CompareCell): string =>
  cell.key ? t(cell.key) : cell.text ?? t('desk.empty_value')

const CHANGED_BG: CSSProperties = { background: 'var(--mantine-color-yellow-0)' }
const CHANGED_BORDER: CSSProperties = { borderLeft: '4px solid var(--mantine-color-yellow-6)' }

export type ShowUndoToastParams = {
  t: Translate
  /** Makes the notification id unique per row. */
  id: number
  message: string
  onUndo: () => void
}

/**
 * The gray 8 s notification with Undo shared by Attach and Dismiss (UI-SPEC
 * §E, §F): every non-approve decision is one click and reversible for 8 s.
 */
export const showUndoToast = ({ t, id, message, onUndo }: ShowUndoToastParams) => {
  const toastId = `desk-undo-${id}-${Date.now()}`
  notifications.show({
    id: toastId,
    color: 'gray',
    autoClose: 8000,
    withCloseButton: true,
    message: (
      <Stack gap={8}>
        <Text size='sm'>{message}</Text>
        <Group gap={8}>
          <Button
            size='xs'
            variant='subtle'
            onClick={() => {
              notifications.hide(toastId)
              onUndo()
            }}
          >
            {t('desk.undo')}
          </Button>
        </Group>
      </Stack>
    )
  })
  return toastId
}

/**
 * Undo for a desk decision (review AD-WR-11): desk_undo with the exact stamp
 * the decision RPC returned, then "Back in {tab}". Never desk_restore: an
 * undo puts the row back where it was and stamps nothing. Resolves with the
 * tab, or null when the undo did not happen (the error is already shown).
 */
export const undoDecision = async (
  t: Translate,
  undo: { mutateAsync: (params: { id: number; decidedAt: string }) => Promise<string> },
  id: number,
  decidedAt: string
): Promise<string | null> => {
  try {
    const tab = await undo.mutateAsync({ id, decidedAt })
    notifications.show({ color: 'green', message: t('desk.undone', { tab: deskTabName(t, tab) }) })
    return tab
  } catch (error) {
    showDeskError(t, error)
    return null
  }
}

const SideBySideRow = ({ row, t }: { row: CompareRow; t: Translate }) => {
  const cell = (side: 'left' | 'right', value: CompareCell): ReactNode => (
    <Box
      data-testid={`match-cell-${side}`}
      data-highlight={row.differs ? 'changed' : undefined}
      data-border={row.differs && side === 'left' ? 'changed' : undefined}
      p={8}
      style={{
        borderRadius: 4,
        ...(row.differs ? CHANGED_BG : {}),
        ...(row.differs && side === 'left' ? CHANGED_BORDER : {})
      }}
    >
      <Group gap={4} justify='space-between' wrap='nowrap'>
        <Text size='xs' lh={1.4} c='dimmed'>
          {t(COMPARE_LABEL_KEYS[row.field])}
        </Text>
        {row.differs && side === 'left' && (
          <Text size='xs' lh={1.4} c='yellow.9'>
            {t('desk.match.differs')}
          </Text>
        )}
      </Group>
      <Text size='sm' style={{ whiteSpace: 'pre-wrap' }}>
        {cellText(t, value)}
      </Text>
    </Box>
  )

  return (
    <SimpleGrid
      cols={2}
      spacing={16}
      data-testid={`match-row-${row.field}`}
      data-differs={row.differs ? 'true' : undefined}
    >
      {cell('left', row.left)}
      {cell('right', row.right)}
    </SimpleGrid>
  )
}

export type MatchPanelProps = {
  application: ApplicationRow
  /** After Attach or Discard: the screen opens the next candidate. */
  onDecided: () => void
  /**
   * 33.1 D-10 / D-32: the desk's duplicate re-check is running (or failed on
   * a desk-relabelled row), so the target may be for an earlier town. The
   * caller decides; Attach, Discard and Not a duplicate wait while true.
   */
  recheckPending?: boolean
  /**
   * Code review WR-03: the candidate as the desk form has it (withFormValues:
   * town and dates), for the left column of a desk re-check result, because
   * the rules matched on those values. Display only: every decision acts on
   * `application`.
   */
  formRow?: ApplicationRow | null
}

/**
 * The duplicate suggestion (DESK-03, D-14, UI-SPEC §E): this candidate beside
 * the published event or pending candidate it may duplicate, with three
 * actions, none of them filled ("Approve event" stays the screen's only filled
 * button).
 *
 * - Attach as extra source: `desk_attach` writes `desk_decision = 'attached'`
 *   on the candidate only; no events row is written (owner ruling: a match is
 *   a suggestion, never a merge). 8 s Undo.
 * - Discard as duplicate: the reject with reason `duplicate` and its toast.
 * - Not a duplicate: `desk_dismiss_match`; the panel goes and the candidate
 *   stays to be approved or rejected.
 *
 * A match whose target a later editor run replaced (label set, both targets
 * null, T-32-08) shows the replaced Alert and only "Not a duplicate".
 *
 * The Paper carries the id `desk-match-panel` (DESK_MATCH_PANEL_ID), so the
 * match checks in "What the editor flagged" scroll to it.
 */
export const MatchPanel = ({ application, onDecided, recheckPending = false, formRow = null }: MatchPanelProps) => {
  const { t, language } = useAppTranslation()
  const queryClient = useQueryClient()
  const { attach, reject, undo, dismissMatch } = useDeskDecision()
  const { venues } = useQueryVenues({})
  const { cities } = useQueryCities()
  const [dismissed, setDismissed] = useState(false)
  const [busy, setBusy] = useState(false)

  const id = application.id
  const eventId = application.match_target_event_id
  const targetApplicationId = eventId === null ? application.match_target_application_id : null
  const replaced = eventId === null && targetApplicationId === null

  const targetEvent = useQuery({
    queryKey: ['desk', 'targetEvent', eventId],
    queryFn: () => queryDeskTargetEvent(eventId as number),
    enabled: eventId !== null
  })
  const targetCandidate = useQuery({
    queryKey: ['desk', 'candidate', targetApplicationId],
    queryFn: () => queryDeskCandidate(targetApplicationId as number),
    enabled: targetApplicationId !== null
  })

  if (dismissed) return null

  const failed = (error: unknown) => {
    showDeskError(t, error)
    if (mapDeskError(error).alreadyDecided) {
      void queryClient.invalidateQueries({ queryKey: ['desk', 'candidate', id] })
    }
  }

  // `then` receives the stamp the decision returned: the Undo hands it back.
  const run = async (write: () => Promise<string>, then: (stamp: string) => void) => {
    setBusy(true)
    let stamp: string
    try {
      stamp = await write()
    } catch (error) {
      setBusy(false)
      failed(error)
      return
    }
    setBusy(false)
    then(stamp)
  }

  const shown = formRow ?? application
  const left = fromApplication(
    shown,
    placeLabelOf(shown.venue_id, shown.city_id, candidatePlaceFallback(shown), venues, cities)
  )

  let right = null
  let targetTitle: string | null = null
  let targetLink: ReactNode = null
  let rightHeader = ''
  if (eventId !== null && targetEvent.data) {
    const event = targetEvent.data
    right = fromEvent(event, placeLabelOf(event.venue, event.city_id, eventPlaceFallback(event), venues, cities))
    targetTitle = eventTitleOf(event, language)
    rightHeader = t('desk.match.published_event')
    targetLink = event.uid ? (
      <Anchor component={Link} to={`/Events/${event.uid}`} size='sm'>
        {t('desk.match.open_event')}
      </Anchor>
    ) : null
  } else if (targetApplicationId !== null && targetCandidate.data) {
    const pending = targetCandidate.data
    right = fromApplication(
      pending,
      placeLabelOf(pending.venue_id, pending.city_id, candidatePlaceFallback(pending), venues, cities)
    )
    targetTitle = pending.event_title?.trim() || null
    rightHeader = t('desk.match.pending_candidate')
    targetLink = (
      <Anchor component={Link} to={`/desk/${pending.id}`} size='sm'>
        {t('desk.match.open_candidate')}
      </Anchor>
    )
  }
  const loadFailed = targetEvent.isError || targetCandidate.isError

  const onAttach = () =>
    run(
      () => attach.mutateAsync(id),
      (stamp) => {
        onDecided()
        showUndoToast({
          t,
          id,
          message: t('desk.match.attached', { title: targetTitle ?? t('desk.untitled') }),
          onUndo: () => void undoDecision(t, undo, id, stamp)
        })
      }
    )

  const onDiscard = () =>
    run(
      () => reject.mutateAsync({ id, reason: 'duplicate' }),
      (stamp) => {
        onDecided()
        showRejectedToast({
          t,
          id,
          title: application.event_title,
          reason: 'duplicate',
          onUndo: () => void undoDecision(t, undo, id, stamp)
        })
      }
    )

  const onDismiss = () =>
    run(
      () => dismissMatch.mutateAsync(id),
      (stamp) => {
        setDismissed(true)
        // Undo clears desk_match_dismissed_at again (desk_undo, AD-WR-11),
        // and the panel comes back.
        showUndoToast({
          t,
          id,
          message: t('desk.match.dismissed'),
          onUndo: () =>
            void undoDecision(t, undo, id, stamp).then((tab) => {
              if (tab !== null) setDismissed(false)
            })
        })
      }
    )

  // 'desk_rules' is a twin of recheckMatch.ts DESK_RULES_MATCH_BY (33.1).
  const caption =
    application.match_by === 'rules'
      ? t('desk.match.by_rules')
      : application.match_by === 'model'
        ? t('desk.match.by_model')
        : application.match_by === 'desk_rules'
          ? t('desk.match.by_desk_rules')
          : null

  const held = busy || recheckPending

  const dismissButton = (
    <Button variant='subtle' color='gray' onClick={onDismiss} disabled={held}>
      {t('desk.match.dismiss')}
    </Button>
  )

  return (
    <Paper
      id={DESK_MATCH_PANEL_ID}
      data-accent='orange'
      withBorder
      p={16}
      style={{ borderLeft: '4px solid var(--mantine-color-orange-6)' }}
    >
      <Stack gap={16}>
        <Group gap={8} align='baseline' wrap='wrap'>
          <Text size='md' fw={600} lh={1.3}>
            {t('desk.match.duplicate_title')}
          </Text>
          {caption && (
            <Text size='xs' lh={1.4} c='dimmed'>
              {caption}
            </Text>
          )}
        </Group>

        {replaced ? (
          <Alert color='gray' title={t('desk.match.replaced_title')}>
            <Text size='sm'>{t('desk.match.replaced_body')}</Text>
          </Alert>
        ) : loadFailed ? (
          <Alert color='red' title={t('desk.error.load')} />
        ) : !right ? (
          <Text size='sm'>{t('common.loading')}</Text>
        ) : (
          <Stack gap={8}>
            <SimpleGrid cols={2} spacing={16}>
              <Text size='md' fw={600} lh={1.3}>
                {t('desk.match.this_candidate')}
              </Text>
              <Group gap={8} align='baseline' wrap='wrap'>
                <Text size='md' fw={600} lh={1.3}>
                  {rightHeader}
                </Text>
                {targetLink}
              </Group>
            </SimpleGrid>
            {sideBySideRows(left, right, dateLocaleOf(language)).map((row) => (
              <SideBySideRow key={row.field} row={row} t={t} />
            ))}
          </Stack>
        )}

        <Group justify='flex-end' gap={8}>
          {!replaced && (
            <>
              <Button
                variant='default'
                leftSection={<IconLinkPlus size={16} />}
                onClick={onAttach}
                disabled={held || !right}
              >
                {t('desk.match.attach')}
              </Button>
              <Button variant='light' color='red' onClick={onDiscard} disabled={held}>
                {t('desk.match.discard')}
              </Button>
            </>
          )}
          {dismissButton}
        </Group>
      </Stack>
    </Paper>
  )
}
